import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem, rgba } from 'polished';
import { Link } from 'react-router';
import { prop } from '../../../utilities';
import { IconType, SvgIcon } from './SvgIcon';
import { __ } from 'react-i18n';

// Interfaces

interface Props {
  totalItems: number;
  offset: number;
  limit: number;
  onOffsetChange?: (newOffset: number) => void;
  query?: string;
  offsetName?: string;
}

interface State {
  totalPages: number;
}

export class Pagination extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      totalPages: Math.ceil(props.totalItems / props.limit),
    };
  }

  public componentDidUpdate(prevProps) {
    if (this.props.totalItems !== prevProps.totalItems) {
      this.setState({
        totalPages: this.calcTotalPages(),
      });
    }
  }

  public render() {
    const { totalPages } = this.state;
    const currentPage = this.countCurrentPage();
    const pageNumbers = this.getPaginationButtons();

    return (
      <Wrapper>
        {totalPages > 1 && (
          <>
            <DesktopPaginationWrapper>
              {currentPage > 1 && this.renderPreviousButton()}
              {pageNumbers}
              {currentPage < totalPages && this.renderNextButton()}
            </DesktopPaginationWrapper>
            <MobilePaginationWrapper>
              {currentPage > 1 && this.renderPreviousButton()}
              <TextPagination>{`${currentPage} z ${totalPages}`}</TextPagination>
              {currentPage < totalPages && this.renderNextButton()}
            </MobilePaginationWrapper>
          </>
        )}
      </Wrapper>
    );
  }

  public getPaginationButtons = (): JSX.Element[] => {
    const paginationButtons: JSX.Element[] = [];
    const currentPage = this.countCurrentPage();
    const { offsetName } = this.props;
    const { totalPages } = this.state;

    if (totalPages <= 7) {
      return this.createPageNumbersAll(currentPage);
    }

    if (currentPage > 2) {
      paginationButtons.push(
        <PaginationItem
          key={1}
          title={1}
          to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
            1,
          )}`.replace(/(offset=).*?(&)/, '')}
        >
          1
        </PaginationItem>,
      );
    }

    if (currentPage > 3) {
      paginationButtons.push(
        <PaginationSpacer key={'space'}>&hellip;</PaginationSpacer>,
      );
    }

    if (currentPage < totalPages) {
      if (currentPage === totalPages) {
        paginationButtons.push(
          <PaginationItem
            key={currentPage - 2}
            title={currentPage - 2}
            to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
              currentPage - 2,
            )}`.replace(/(offset=).*?(&)/, '')}
          >
            {currentPage - 2}
          </PaginationItem>,
        );
      }

      if (currentPage !== 1) {
        paginationButtons.push(
          <PaginationItem
            key={currentPage - 1}
            title={currentPage - 1}
            to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
              currentPage - 1,
            )}`.replace(/(offset=).*?(&)/, '')}
          >
            {currentPage - 1}
          </PaginationItem>,
        );
      }

      paginationButtons.push(
        <PaginationItem
          key={currentPage}
          title={currentPage}
          to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
            currentPage,
          )}`.replace(/(offset=).*?(&)/, '')}
          active={true}
        >
          {currentPage}
        </PaginationItem>,
      );

      if (currentPage < totalPages - 1) {
        paginationButtons.push(
          <PaginationItem
            key={currentPage + 1}
            title={currentPage + 1}
            to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
              currentPage + 1,
            )}`.replace(/(offset=).*?(&)/, '')}
          >
            {currentPage + 1}
          </PaginationItem>,
        );
      }

      if (currentPage === 1) {
        paginationButtons.push(
          <PaginationItem
            key={currentPage + 2}
            title={currentPage + 2}
            to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
              currentPage + 2,
            )}`.replace(/(offset=).*?(&)/, '')}
          >
            {currentPage + 2}
          </PaginationItem>,
        );
      }
    }

    if (currentPage < totalPages - 2) {
      paginationButtons.push(
        <PaginationSpacer key={'spacer2'}>&hellip;</PaginationSpacer>,
      );
    }

    if (currentPage === totalPages) {
      paginationButtons.push(
        <PaginationItem
          key={totalPages - 2}
          title={totalPages - 2}
          to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
            totalPages - 2,
          )}`.replace(/(offset=).*?(&)/, '')}
        >
          {totalPages - 2}
        </PaginationItem>,
      );
    }

    if (currentPage === totalPages) {
      paginationButtons.push(
        <PaginationItem
          key={totalPages - 1}
          title={totalPages - 1}
          to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
            totalPages - 1,
          )}`.replace(/(offset=).*?(&)/, '')}
        >
          {totalPages - 1}
        </PaginationItem>,
      );
    }

    paginationButtons.push(
      <PaginationItem
        key={totalPages}
        title={totalPages}
        to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
          totalPages,
        )}`.replace(/(offset=).*?(&)/, '')}
        active={currentPage === totalPages}
      >
        {totalPages}
      </PaginationItem>,
    );

    return paginationButtons;
  };

  public createPageNumbersAll(currentPage) {
    const { totalPages } = this.state;
    const paginationButtons: JSX.Element[] = [];
    const { offsetName } = this.props;

    for (let i = 1; i <= totalPages; i++) {
      paginationButtons.push(
        <PaginationItem
          key={i}
          title={i}
          to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
            i,
          )}`.replace(/(offset=).*?(&)/, '')}
          active={currentPage === i}
        >
          {i}
        </PaginationItem>,
      );
    }

    return paginationButtons;
  }

  public renderPreviousButton = (): JSX.Element => {
    const { totalPages } = this.state;
    const currentPage = this.countCurrentPage();
    const { offsetName } = this.props;

    return (
      <PreviousButton
        key={totalPages}
        title={totalPages}
        to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
          currentPage - 1,
        )}`.replace(/(offset=).*?(&)/, '')}
      >
        <SvgIcon
          icon={IconType.leftArrow}
          width={16}
          height={16}
          viewBox="0 0 16 16"
          cursor="pointer"
        />
      </PreviousButton>
    );
  };

  public renderNextButton = (): JSX.Element => {
    const { totalPages } = this.state;
    const currentPage = this.countCurrentPage();
    const { offsetName } = this.props;

    return (
      <NextButton
        key={totalPages}
        title={totalPages}
        to={`${this.props.query}&${offsetName || 'offset'}=${this.getOffset(
          currentPage + 1,
        )}`.replace(/(offset=).*?(&)/, '')}
      >
        <SvgIcon
          icon={IconType.rightArrow}
          width={16}
          height={16}
          viewBox="0 0 16 16"
          cursor="pointer"
        />
      </NextButton>
    );
  };

  public calcTotalPages = () => {
    return Math.ceil(this.props.totalItems / this.props.limit);
  };

  public countCurrentPage = () => {
    return Math.ceil(this.props.offset / this.props.limit + 1);
  };

  public getOffset = page => {
    const diff = parseInt(page, 10) - this.countCurrentPage();
    return parseInt(this.props.offset.toString(), 10) + diff * this.props.limit;
  };
}

const Wrapper = styled.div``;

const PaginationItemStyles = css`
  display: inline-block;
  border-radius: ${rem(4)};
  text-align: center;
  padding: 0;
`;

const PaginationItem = styled(Link)<{ active?: boolean }>`
  ${PaginationItemStyles};
  text-decoration: none;
  padding: ${rem(13)} 0;
  width: ${rem(48)};
  line-height: ${rem(18)};
  font-size: ${rem(14)};
  font-family: ${({ theme }) => theme.font.primary};

  font-weight: ${props => (props.active ? 600 : 500)};
  cursor: pointer;
  ${({ theme }) => theme.transitions('background-color, color')};
  color: ${props =>
    props.active
      ? ({ theme }) => theme.color.white
      : ({ theme }) => theme.colors.textPrimary};
  background-color: ${props =>
    props.active
      ? props.theme.colors.primary
      : rgba(props.theme.colors.primary, 0.12)};
  margin-right: ${rem(8)};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.color.white};
    font-weight: 600;
  }
`;

const PaginationSpacer = styled.span`
  ${PaginationItemStyles};
  width: ${rem(19)};
  padding-right: ${rem(20)};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  font-weight: 400;
  line-height: ${rem(22)};
`;

const NextButton = styled(PaginationItem)`
  svg {
    fill: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const PreviousButton = styled(NextButton)``;

const TextPagination = styled.p`
  font-size: ${rem(14)};
  font-weight: 500;
  line-height: ${rem(21)};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: none;
  margin: 0 ${rem(24)};
  text-transform: lowercase;
  font-family: ${({ theme }) => theme.font.primary};

  ${({ theme }) => theme.mediab.m580`
    display: block;
  `}
`;

const DesktopPaginationWrapper = styled.div`
  text-align: center;
  margin: 0 auto;
  margin-top: ${rem(32)};
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ theme }) => theme.mediab.m580`
    display: none;
  `}
`;

const MobilePaginationWrapper = styled(DesktopPaginationWrapper)`
  display: none;

  ${({ theme }) => theme.mediab.m580`
    display: flex;
  `}

  ${PreviousButton} {
    margin-right: 0;
  }
`;
